<template>
  <modal-wrap
    :id="id"
    ref="integrationsMagentoKeys"
    :name="modalName"
    :header-class="'integrations-magento-keys-header'"
    :hide-footer="true"
    body-class="integrations-magento-keys-modal"
    size="lg"
  >
    <template #modal-title>
      <div class="d-flex justify-content-between align-items-center">
        <h4 class="mb-0">{{ modalTitle }}</h4>
      </div>
    </template>
    <template #default>
      <b-form-group>
        <div class="form-group">
          <div class="row">
            <div class="col-md-12">
              <div class="form-group">
                <label><b>Credentials</b></label>
              </div>
            </div>
          </div>

          <div class="row">
            <div class="col-md-12">
              <div class="form-group">
                <label>Site Url</label>
                <VInput v-model="url" type="text" name="url" :rules="{ required: true, min: 10 }" />
              </div>
            </div>
          </div>

          <div class="row">
            <div class="col-md-12">
              <div class="form-group">
                <label>Key</label>
                <VInput v-model="key" type="text" name="key" :rules="{ required: true, min: 12 }" placeholder="..." />
              </div>
            </div>
          </div>

          <div class="d-flex justify-content-between align-items-center">
            <h4 class="mb-0">Filter</h4>
          </div>

          <div class="row">
            <div class="col-md-12">
              <div class="form-group">
                <label>Shipment method</label>
                <VInput v-model="shipmentMethod" type="text" name="shipmentMethod" placeholder="PacSort" />
              </div>
            </div>
          </div>

          <div class="d-flex justify-content-between align-items-center">
            <h4 class="mb-0">Parameters</h4>
          </div>

          <div class="row">
            <div class="col-md-6">
              <div class="form-group">
                <label>Import Type</label>
                <div class="row">
                  <div class="col-12 col-md-6">
                    <input id="scheduled" v-model="importType" type="radio" value="scheduled" />
                    <label for="scheduled" class="radio-group">
                      <span class="radio-title">Scheduled</span>
                    </label>
                  </div>
                  <div class="col-12 col-md-6">
                    <input id="on_demand" v-model="importType" type="radio" value="on_demand" />
                    <label for="on_demand" class="radio-group">
                      <span class="radio-title">On Demand</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
            <div class="col-md-6">
              <div class="form-group">
                <label>Interval (minutes)</label>
                <VInput
                  v-model="importInterval"
                  type="text"
                  name="import_interval"
                  :formatter="numbersOnly"
                  :rules="{ required: true, min: 1 }"
                />
              </div>
            </div>
          </div>

          <div class="row">
            <div class="col-md-6">
              <div class="form-group">
                <label>Default Weight (g)</label>
                <VInput
                  v-model="defaultWeight"
                  type="text"
                  name="default_weight"
                  placeholder="Default Weight"
                  :formatter="numbersOnly"
                  :rules="{ required: true, min: 1 }"
                />
              </div>
            </div>
            <div class="col-md-6">
              <div class="form-group">
                <label>Default Height (cm)</label>
                <VInput
                  v-model="defaultHeight"
                  type="text"
                  name="default_height"
                  placeholder="Default Height"
                  :formatter="numbersOnly"
                  :rules="{ required: true, min: 1 }"
                />
              </div>
            </div>
          </div>

          <div class="row">
            <div class="col-md-6">
              <div class="form-group">
                <label>Default Width (cm)</label>
                <VInput
                  v-model="defaultWidth"
                  type="text"
                  name="default_width"
                  placeholder="Default Width"
                  :formatter="numbersOnly"
                  :rules="{ required: true, min: 1 }"
                />
              </div>
            </div>
            <div class="col-md-6">
              <div class="form-group">
                <label>Default Length (cm)</label>
                <VInput
                  v-model="defaultLength"
                  type="text"
                  name="default_length"
                  placeholder="Default Length"
                  :formatter="numbersOnly"
                  :rules="{ required: true, min: 1 }"
                />
              </div>
            </div>
          </div>

          <div class="row">
            <div class="col-md-12">
              <div class="form-group">
                <b-form-checkbox v-model="active" :value="active"> Active </b-form-checkbox>
              </div>
            </div>
          </div>
        </div>

        <div class="form-group">
          <button type="button" class="btn create-btn btn-green" :disabled="isButtonDisabled" @click="connect">
            Connect
          </button>
        </div>
      </b-form-group>
    </template>
  </modal-wrap>
</template>

<script lang="ts">
import type { MagentoIntegrationModal } from '@/types/Models/Integration'
import { notify } from '@kyvg/vue3-notification'
import { BFormCheckbox, BFormGroup } from 'bootstrap-vue-next'
import { defineComponent, ref, computed, watch, type ComputedRef, type Ref, toRaw } from 'vue'
import { MAGENTO } from '@/const/IntegrationTypes'
import { MAGENTO_KEYS } from '@/const/ModalTypes'
import { numbersOnly } from '@/services/Helpers'
import { useCustomersIntegrationsStore } from '@/store/customers/integrations'
import { useVueNextModalStore } from '@/store/vueNextModal'
import VInput from '@/views/Components/Elements/Form/VInput.vue'
import ModalWrap from '@/views/Modals/ModalWrap.vue'

export default defineComponent({
  name: 'IntegrationsMagentoKeysModal',
  components: { BFormCheckbox, BFormGroup, ModalWrap, VInput },
  props: {
    id: {
      type: String,
      required: true,
    },
  },
  setup() {
    const modalStore = useVueNextModalStore()
    const customersIntegrationsStore = useCustomersIntegrationsStore()
    const url: Ref<string> = ref('')
    const key: Ref<string> = ref('')
    const shipmentMethod: Ref<string> = ref('')
    const importInterval: Ref<number> = ref(0)
    const importType: Ref<string> = ref('scheduled')
    const active: Ref<boolean> = ref(true)
    const defaultWeight: Ref<number> = ref(500)
    const defaultLength: Ref<number> = ref(34)
    const defaultWidth: Ref<number> = ref(24)
    const defaultHeight: Ref<number> = ref(7)
    const isButtonDisabled: Ref<boolean> = ref(false)

    const modalTitle = 'Magento keys configuration'
    const modalName = MAGENTO_KEYS

    const modalData: ComputedRef<MagentoIntegrationModal | null> = computed(
      () => modalStore.modalPropsByName(modalName) || null,
    )
    const customerId: ComputedRef<string> = computed(() => modalData.value?.customerId || '')

    const connect = () => {
      if (isButtonDisabled.value) return
      isButtonDisabled.value = true
      customersIntegrationsStore
        .connect({
          customerId: customerId.value,
          data: {
            marketplace: MAGENTO,
            import_interval: importInterval.value,
            import_type: importType.value,
            active: active.value,
            default_weight: defaultWeight.value,
            default_length: defaultLength.value,
            default_width: defaultWidth.value,
            default_height: defaultHeight.value,
            credentials: {
              url: url.value,
              key: key.value,
            },
            configuration: {
              shipment_method: shipmentMethod.value,
            },
          },
        })
        .then((response) => {
          if (response.message) {
            notify({ group: 'error', text: response.message })
            isButtonDisabled.value = false
          } else {
            notify({ group: 'success', text: 'The integration has been connected.' })
            customersIntegrationsStore.fetch(toRaw(customerId.value))
            modalStore.closeModal(modalName)
          }
        })
        .catch(() => {
          isButtonDisabled.value = false
        })
    }

    watch(modalData, () => {
      if (!modalData.value) return
      url.value = modalData.value.url
      key.value = modalData.value.key
      defaultWidth.value = modalData.value.defaultWidth
      defaultWeight.value = modalData.value.defaultWeight
      defaultLength.value = modalData.value.defaultLength
      defaultHeight.value = modalData.value.defaultHeight
      shipmentMethod.value = modalData.value.configuration?.shipment_method
      importInterval.value = modalData.value.importInterval
      importType.value = modalData.value.importType
      active.value = modalData.value.active
    })

    return {
      url,
      key,
      shipmentMethod,
      importInterval,
      importType,
      active,
      defaultWeight,
      defaultLength,
      defaultWidth,
      defaultHeight,
      isButtonDisabled,
      modalName,
      modalTitle,
      numbersOnly,
      connect,
    }
  },
})
</script>
